import { createContext, useContext, useEffect, useState, useCallback, ReactNode, useRef } from "react";
import { User, Session, AuthChangeEvent } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { identifyUser, resetUser } from "@/lib/posthog";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  lastEvent: AuthChangeEvent | null;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastEvent, setLastEvent] = useState<AuthChangeEvent | null>(null);

  /**
   * If the user previously opted in to web push, silently re-register the
   * service worker so the subscription stays active. We only do this once per
   * AuthProvider mount to avoid duplicate work.
   */
  const webPushChecked = useRef(false);
  const reRegisterWebPush = useCallback(async (userId: string) => {
    if (webPushChecked.current) return;
    webPushChecked.current = true;

    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

      // Check if user has any web push subscriptions in the DB
      const { data } = await supabase
        .from("web_push_subscriptions")
        .select("id")
        .eq("user_id", userId)
        .limit(1);

      if (!data || data.length === 0) return;

      // User previously opted in — ensure SW is registered
      const existing = await navigator.serviceWorker.getRegistration("/sw.js");
      if (!existing) {
        await navigator.serviceWorker.register("/sw.js");
      }
    } catch (e) {
      console.warn("[Auth] Failed to re-register web push SW:", e);
    }
  }, []);

  useEffect(() => {
    // Detect if we're returning from an OAuth redirect
    // PKCE flow (Supabase JS v2+): uses ?code= query param
    // Implicit flow (legacy): uses #access_token hash
    const url = new URL(window.location.href);
    const hasOAuthCode = url.searchParams.has('code');
    const hasOAuthHash = window.location.hash.includes('access_token') || window.location.hash.includes('refresh_token');
    const isOAuthReturn = hasOAuthCode || hasOAuthHash;

    // Get the initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      // If returning from OAuth but getSession returned null,
      // Supabase is still exchanging the code for a session.
      // Keep loading=true — onAuthStateChange will fire with the real session.
      if (!session && isOAuthReturn) {
        return;
      }
      setIsLoading(false);
    });

    // Single auth state change listener for the entire app
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLastEvent(event);
      setIsLoading(false);

      // PostHog tracking (moved from App.tsx)
      if (event === "SIGNED_IN" && session?.user) {
        identifyUser(session.user.id, {
          email: session.user.email,
          created_at: session.user.created_at,
        });

        // Auto-re-register service worker for web push if user previously opted in
        reRegisterWebPush(session.user.id);
      } else if (event === "SIGNED_OUT") {
        resetUser();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = useCallback(async () => {
    // 1) Local signout (immediate)
    await supabase.auth.signOut({ scope: "local" });

    // 2) Purge persisted auth tokens
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith("sb-") || k.includes("supabase"))
        .forEach((k) => localStorage.removeItem(k));
    } catch (e) {
      console.warn("[Auth] Failed to purge localStorage tokens:", e);
    }

    // 3) Best-effort global signout in the background
    setTimeout(() => {
      supabase.auth.signOut({ scope: "global" }).catch((e) => {
        console.warn("[Auth] Global signOut warning (safe to ignore):", e);
      });
    }, 0);
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, isLoading, lastEvent, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
